'use client'
// Flow lines: a small mass-flow (Sankey-style) figure. Each input draws a thin curve into the
// middle; curves fan out from the middle to each output. The middle is a node (a stage's key
// topics) or, when there is none, a junction point. Either side may be empty.
// Layout ("common axis", Connor 2026-10-13): a fixed column grid with the captions in one row; the
// three columns are then shifted so they centre on one horizontal axis (as in a tidy tree, a parent
// sits at the midpoint of its children), so the fans open both ways and stay short. Curves meet the
// text 8 px from it, at the centre of each item's first line. Phones stack the parts with no curves.
import React, { useLayoutEffect, useRef, useState } from 'react'

type Path = { d: string; end: [number, number] }

const lineRects = (e: Element) => {
  const rg = document.createRange()
  rg.selectNodeContents(e)
  return Array.from(rg.getClientRects()).filter((r) => r.width > 0)
}
// nearly level: draw it straight, a 1-2 px tilt reads as a mistake
const curve = (x1: number, y1: number, x2: number, y2: number) =>
  Math.abs(y1 - y2) < 4
    ? `M${x1},${y1} L${x2},${y1}`
    : `M${x1},${y1} C${(x1 + x2) / 2},${y1} ${(x1 + x2) / 2},${y2} ${x2},${y2}`

export function FlowLines({
  ins, outs, nodeLabel, nodeItems, label, wideNode = false, inLabel = 'Goes in',
}: {
  ins: React.ReactNode[]
  outs: React.ReactNode[]
  nodeLabel?: string
  nodeItems?: React.ReactNode[]
  label: string
  /** the stage figure: the node lists the steps, so it gets a wider column */
  wideNode?: boolean
  /** the caption over what goes in */
  inLabel?: string
}) {
  const box = useRef<HTMLDivElement>(null)
  const [paths, setPaths] = useState<Path[]>([])
  const [size, setSize] = useState({ w: 0, h: 0 })
  const hasNode = !!nodeItems?.length

  useLayoutEffect(() => {
    const el = box.current
    if (!el) return
    const draw = () => {
      const cols = [el.querySelector<HTMLElement>('.hb-fl-c-in'), el.querySelector<HTMLElement>('.hb-fl-c-node, .hb-fl-j'), el.querySelector<HTMLElement>('.hb-fl-c-out')]
      cols.forEach((c) => c && (c.style.marginTop = ''))
      if (getComputedStyle(el).display !== 'grid') { setPaths([]); return } // phones: stacked, no curves
      let o = el.getBoundingClientRect()
      const firstY = (t: Element) => { const f = lineRects(t)[0]; return f ? f.top + f.height / 2 - o.top : 0 }
      // each column's anchor: a list = midpoint of its first and last item; the node = the middle of
      // its text; the junction = its own top (it has no height to centre)
      const anchor = (c: HTMLElement | null) => {
        if (!c) return null
        if (c.classList.contains('hb-fl-j')) return c.getBoundingClientRect().top - o.top + 4
        const items = Array.from(c.querySelectorAll('.hb-fl-t'))
        if (items.length) { const ys = items.map(firstY); return ((ys[0] ?? 0) + (ys[ys.length - 1] ?? 0)) / 2 }
        const rs = lineRects(c)
        if (!rs.length) return null
        return (Math.min(...rs.map((r) => r.top)) + Math.max(...rs.map((r) => r.bottom))) / 2 - o.top
      }
      const as = cols.map(anchor)
      const ax = Math.max(...as.filter((a): a is number => a != null))
      cols.forEach((c, i) => { if (c && as[i] != null) c.style.marginTop = `${ax - (as[i] as number)}px` })
      o = el.getBoundingClientRect()
      let nl: number, nr: number
      const mid = cols[1]
      if (!mid) return
      if (hasNode) {
        const rs = lineRects(mid)
        nl = Math.min(...rs.map((r) => r.left)) - o.left - 8
        nr = Math.max(...rs.map((r) => r.right)) - o.left + 8
      } else {
        const j = mid.getBoundingClientRect()
        nl = nr = j.left + j.width / 2 - o.left
      }
      const out: Path[] = []
      el.querySelectorAll('.hb-fl-in .hb-fl-t').forEach((t) => {
        const rs = lineRects(t); if (!rs.length) return
        const x = Math.max(...rs.map((r) => r.right)) - o.left + 8, y = rs[0]!.top + rs[0]!.height / 2 - o.top
        out.push({ d: curve(x, y, nl, ax), end: [x, y] })
      })
      el.querySelectorAll('.hb-fl-out .hb-fl-t').forEach((t) => {
        const rs = lineRects(t); if (!rs.length) return
        const x = Math.min(...rs.map((r) => r.left)) - o.left - 8, y = rs[0]!.top + rs[0]!.height / 2 - o.top
        out.push({ d: curve(nr, ax, x, y), end: [x, y] })
      })
      setPaths(out)
      setSize({ w: o.width, h: o.height })
    }
    draw()
    document.fonts?.ready.then(draw) // measured lines shift once Inter has loaded
    const ro = new ResizeObserver(draw)
    ro.observe(el)
    return () => ro.disconnect()
  }, [ins.length, outs.length, hasNode])

  const list = (xs: React.ReactNode[], cls: string) => (
    <ul className={`hb-fl-list ${cls}`}>{xs.map((x, i) => <li key={i}><span className="hb-fl-t">{x}</span></li>)}</ul>
  )
  const shape = [
    'hb-fl-grid',
    !hasNode ? 'hb-fl-nonode' : wideNode ? 'hb-fl-widenode' : '',
    !ins.length ? 'hb-fl-noin' : '',
    !outs.length ? 'hb-fl-noout' : '',
  ].filter(Boolean).join(' ')
  return (
    <figure className="hb-fl" aria-label={label}>
      <div className={shape} ref={box}>
        {ins.length > 0 && <p className="hb-fl-k hb-fl-k-in">{inLabel}</p>}
        {hasNode && <p className="hb-fl-k hb-fl-k-node">{nodeLabel}</p>}
        {outs.length > 0 && <p className="hb-fl-k hb-fl-k-out">Comes out</p>}
        {ins.length > 0 && <div className="hb-fl-c hb-fl-c-in">{list(ins, 'hb-fl-in')}</div>}
        {hasNode
          ? <div className="hb-fl-c hb-fl-c-node"><div className="hb-fl-node">{nodeItems!.map((x, i) => <span key={i}>{x}</span>)}</div></div>
          : <span className="hb-fl-j" aria-hidden="true" />}
        {outs.length > 0 && <div className="hb-fl-c hb-fl-c-out">{list(outs, 'hb-fl-out')}</div>}
        {paths.length > 0 && (
          <svg className="hb-fl-svg" width={size.w} height={size.h} aria-hidden="true" focusable="false">
            {paths.map((p, i) => <path key={i} d={p.d} />)}
            {paths.map((p, i) => <circle key={`c${i}`} cx={p.end[0]} cy={p.end[1]} r="2.5" />)}
          </svg>
        )}
      </div>
    </figure>
  )
}
